import { useCallback, useEffect, useMemo, useRef, useState, type RefObject } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { loadCore } from "./core";
import { needsSignIn } from "@common/api";
import { setPref } from "@common/preferences";
import { downloadCloudRom, listCloudRoms, uploadToLibrary } from "../library/cloudLibrary";
import { libraryKeys } from "../library/queries";
import { applyKeymap, bindKeyboardExtras } from "./keymap";
import {
  type EjsSession,
  type EjsSystem,
  ejsRoms,
  ejsSystemOf,
  ejsSystemOfName,
  isEjsSystem,
  isRetiredGame,
  retiredMessage,
  rememberSystem,
  rememberedSystem,
  startEjs,
} from "./ejs";
import { here, isNative } from "../desktop/desktop";
import {
  displayName,
  downloadBytes,
  fileExtension,
  isRomFile,
  isSaveFileFor,
  romBaseName,
  saveFileName,
  stateFileName,
  stateSlotOf,
} from "./saves";
import { type GameKind, type SaveStats, gameBoyTitle, gameKindFromTitle, parseSaveStats } from "./saveStats";
import { ejsStates } from "./ejsStates";
import { keepSave, sameBytes, type KeptSource } from "./keptSaves";
import { slotStore, type SlotStats } from "./slots";
import { type CheatId, type CheatPreset, cheatPresets, cheatsFileText, loadCheatChoice, saveCheatChoice } from "./cheats";
import { romGameCode } from "../guides/detection";
import type { MgbaCore } from "./types";
import { flushCloudSync, flushCloudSyncBeforeLeaving, syncNow } from "./useCloudSync";
import { session } from "../auth/session";

// mGBA for the Game Boy family; EmulatorJS for N64.
export type Engine = "mgba" | "ejs";

export type CoreStatus = "booting" | "ready" | "unsupported" | "error";

export interface LocalSave {
  name: string;
  updatedAt: Date;
  bytes: Uint8Array;
}

export interface Emulator {
  core: MgbaCore | null;
  status: CoreStatus;
  /** Which emulator the running game is on; null when nothing is running. */
  engine: Engine | null;
  version: string | null;
  error: string | null;
  notice: string | null;
  library: string[];
  /** An EmulatorJS game between being picked and running (its core and data load). */
  starting: { name: string; system: EjsSystem } | null;
  /** A game named in the URL is being fetched from the cloud library. */
  fetching: { name: string; fraction: number } | null;
  current: string | null;
  paused: boolean;
  fastForward: number;
  /** Fast-forward latched on by the toggle (Space still works as a hold). */
  fastForwardOn: boolean;
  toggleFastForward(): void;
  /** Which EmulatorJS system is running, when the engine is EmulatorJS. */
  ejsSystem: EjsSystem | null;
  /** EmulatorJS only: show or hide its menu bar. */
  setEjsMenu(open: boolean): void;
  /** Turbo button: run at the chosen speed while held, then back to the toggle's state. */
  holdFastForward(down: boolean): void;
  setFastForward(multiplier: number): void;
  activeSlot: number | null;
  /** Registered state slots for the current game, ascending. One by default. */
  slots: number[];
  addSlot(): void;
  /** Deletes the slot's state file and forgets its name and stats. */
  removeSlot(slot: number): Promise<void>;
  slotNames: Record<number, string>;
  renameSlot(slot: number, name: string): void;
  slotLabel(slot: number): string;
  stateSavedAt(slot: number): Date | null;
  /** Trainer, play time and Pokédex count read from the battery save, when the game is a known Pokémon. */
  saveStats(): SaveStats | null;
  /** Snapshot of saveStats at the moment each slot was last written. */
  slotStats: Record<number, SlotStats>;
  muted: boolean;
  toggleMute(): void;
  /** When the game stopped advancing (pause or hidden tab); null while running. */
  idleSince: Date | null;
  /** Epoch ms of the last keyboard or touch input. */
  lastInputAt(): number;
  captureKeyboard(enabled: boolean): void;
  autoSave: boolean;
  lastAutoSave: Date | null;
  setAutoSave(on: boolean): void;
  addRom(file: File): Promise<void>;
  deleteRom(name: string): Promise<void>;
  play(name: string): Promise<boolean>;
  stop(): Promise<void>;
  togglePause(): void;
  exportSave(): void;
  importSaveToSlot(slot: number, file: File): Promise<void>;
  currentSaveBytes(): Uint8Array | null;
  localSaves(): LocalSave[];
  /** Goes up each time the running game's battery save or a state is written. */
  saveRevision: number;
  /** Replaces a save file of the running game; false when nothing was running to take it. */
  importCloudFile(name: string, bytes: Uint8Array): Promise<boolean>;
  saveState(slot: number): Promise<void>;
  /** Cheat presets the running game has (mGBA Pokémon games), and which are on. */
  cheats: CheatPreset[];
  cheatChoice: CheatId[];
  /** Turns presets on or off; the game reloads in place with the new set. */
  setCheats(ids: CheatId[]): Promise<void>;
  /** Writes the active slot (slot 1 if none) and uploads the game's saves right away. */
  /** Writes the active slot and uploads it now; how far that got, for the button to show. */
  quickSave(): Promise<"synced" | "local" | "failed">;
  /** Presses one of the running EmulatorJS game's controls (N64Move). */
  ejsInput(index: number, value: number): void;
  /** Resets the console (multi-game carts go back to their menu), running again if paused. */
  resetConsole(): Promise<void>;
  loadState(slot: number): void;
}

const SYNC_DEBOUNCE_MS = 500;

// The battery save a write is about to replace goes to the kept saves
// first; if it can't be kept, the write doesn't happen.
function keepMgbaBattery(core: MgbaCore, name: string, replacing: Uint8Array, source: KeptSource) {
  const path = `${core.filePaths().savePath}/${saveFileName(name)}`;
  const onDisk = core.FS.analyzePath(path).exists;
  const current = core.getSave() ?? (onDisk ? core.FS.readFile(path) : null);
  if (!current || sameBytes(current, replacing)) return Promise.resolve();
  return keepSave(name, current, source, onDisk ? core.FS.stat(path).mtime.getTime() : null);
}
const FAST_FORWARD_KEY = "pocket-play.fastForward";
const AUTO_SAVE_KEY = "pocket-play.autoSave";
const MUTED_KEY = "pocket-play.muted";
export const FAST_FORWARD_OPTIONS = [2, 3, 4, 5, 8];
export const AUTO_SAVE_MS = 30_000;
// No keyboard or touch input for this long means nothing new to save.
export const USER_IDLE_MS = 60_000;

// A state slot nobody plays in, to carry the position across a reload.
const SCRATCH_SLOT = 99;

// The slot to open a game on: the one asked for, when the game has it, else its last.
const slotToOpen = (name: string, slot: number | null) =>
  slot && slotStore.list(name).includes(slot) ? slot : slotStore.active(name);

function storedFastForward(): number {
  const n = Number(localStorage.getItem(FAST_FORWARD_KEY));
  return FAST_FORWARD_OPTIONS.includes(n) ? n : FAST_FORWARD_OPTIONS[0];
}

export function useEmulator(canvasRef: RefObject<HTMLCanvasElement | null>): Emulator {
  const queryClient = useQueryClient();
  const [core, setCore] = useState<MgbaCore | null>(null);
  const [status, setStatus] = useState<CoreStatus>("booting");
  const [version, setVersion] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [mgbaRoms, setMgbaRoms] = useState<string[]>([]);
  const [ejsNames, setEjsNames] = useState<string[]>([]);
  const [ejsReady, setEjsReady] = useState(false);
  const [ejsSystem, setEjsSystem] = useState<EjsSystem | null>(null);
  const [starting, setStarting] = useState<{ name: string; system: EjsSystem } | null>(null);
  const [engine, setEngine] = useState<Engine | null>(null);
  const [current, setCurrent] = useState<string | null>(null);
  const [paused, setPaused] = useState(false);
  const [fastForward, setFastForwardState] = useState(storedFastForward);
  const [activeSlot, setActiveSlot] = useState<number | null>(null);
  const [slots, setSlots] = useState<number[]>([1]);
  const [slotNames, setSlotNames] = useState<Record<number, string>>({});
  const [slotStats, setSlotStats] = useState<Record<number, SlotStats>>({});
  // When each slot's state was written, for EmulatorJS games (mGBA's come
  // from its filesystem).
  const [ejsStateTimes, setEjsStateTimes] = useState<Record<number, Date>>({});
  const [muted, setMuted] = useState(() => localStorage.getItem(MUTED_KEY) === "1");
  const gameKind = useRef<GameKind | null>(null);
  const gameCode = useRef<string | null>(null);
  const [cheats, setCheatsState] = useState<CheatPreset[]>([]);
  const [cheatChoice, setCheatChoice] = useState<CheatId[]>([]);
  const [idleSince, setIdleSince] = useState<Date | null>(null);
  const [autoSave, setAutoSaveState] = useState(() => localStorage.getItem(AUTO_SAVE_KEY) === "1");
  const [lastAutoSave, setLastAutoSave] = useState<Date | null>(null);
  const [saveRevision, setSaveRevision] = useState(0);
  const bumpSaves = useCallback(() => setSaveRevision((n) => n + 1), []);

  const library = useMemo(() => [...mgbaRoms, ...ejsNames].sort(), [mgbaRoms, ejsNames]);

  const syncTimer = useRef<number | undefined>(undefined);
  // EmulatorJS can't be torn down, so once one of its games has started this page
  // is its page: every other play or stop is a navigation.
  const ejsSession = useRef<EjsSession | null>(null);
  const engineRef = useRef<Engine | null>(null);
  engineRef.current = engine;
  // The router's search, not window.location's: the sync effect below
  // rewrites the address bar without telling the router, and the sign-in
  // return navigates to the saved path after mount, which must be honored.
  const routerSearch = useLocation().search;
  const handledSearch = useRef<string | null>(null);
  const fetchedFor = useRef<string | null>(null);
  const [fetching, setFetching] = useState<{ name: string; fraction: number } | null>(null);
  const [mountedAt] = useState(Date.now);
  const lastInput = useRef(mountedAt);
  const userPaused = useRef(false);
  const currentRef = useRef<string | null>(null);
  currentRef.current = current;
  const activeSlotRef = useRef<number | null>(null);
  activeSlotRef.current = activeSlot;
  const fastForwardRef = useRef(fastForward);
  fastForwardRef.current = fastForward;
  const [fastForwardOn, setFastForwardOn] = useState(false);
  const fastForwardOnRef = useRef(false);
  fastForwardOnRef.current = fastForwardOn;
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const autoSaveRef = useRef(autoSave);
  autoSaveRef.current = autoSave;
  const holdFastForwardRef = useRef<(down: boolean) => void>(() => {});
  // Leaving an EmulatorJS game is a page load; this writes its active slot
  // first when auto-save is on, as stopping an mGBA game does.
  const beforeLeaving = useRef<(() => Promise<unknown>) | null>(null);

  const flash = useCallback((text: string) => {
    setNotice(text);
    window.setTimeout(() => setNotice((n) => (n === text ? null : n)), 2500);
  }, []);

  // /data is an IDBFS mount without autoPersist: nothing written there
  // reaches IndexedDB until FSSync. Battery saves change constantly while a
  // game runs, so coalesce.
  const scheduleSync = useCallback((c: MgbaCore) => {
    window.clearTimeout(syncTimer.current);
    syncTimer.current = window.setTimeout(() => {
      c.FSSync().then(bumpSaves, () => setError(`Could not persist save data to ${here}.`));
    }, SYNC_DEBOUNCE_MS);
  }, [bumpSaves]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    if (!window.crossOriginIsolated) {
      // Only /play is served cross-origin isolated; reached without a page
      // load (a router link someone added), it isn't yet. Once per address.
      const tried = "pocket-play.isolationReload";
      if (sessionStorage.getItem(tried) !== window.location.href) {
        sessionStorage.setItem(tried, window.location.href);
        window.location.reload();
        return;
      }
      setStatus("unsupported");
      return;
    }
    let cancelled = false;
    loadCore(canvas)
      .then((c) => {
        if (cancelled) return;
        c.setCoreSettings({
          rewindEnable: false,
          autoSaveStateEnable: false,
          restoreAutoSaveStateOnLoad: false,
        });
        c.addCoreCallbacks({
          saveDataUpdatedCallback: () => scheduleSync(c),
          coreCrashedCallback: () => setError("The emulator core crashed. Reload the page."),
        });
        setCore(c);
        setVersion(`${c.version.projectName} ${c.version.projectVersion}`);
        setMgbaRoms(c.listRoms());
        setStatus("ready");
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        setError(e instanceof Error ? e.message : String(e));
        setStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [canvasRef, scheduleSync]);

  useEffect(() => {
    ejsRoms.list().then(setEjsNames, () => {}).finally(() => setEjsReady(true));
  }, []);

  // Browsers won't start audio without a gesture; SDL creates the context
  // when the first game loads, which happens inside a click, but a stale
  // suspended context can survive a reload of the page state. The same
  // events are the activity signal auto-save and the freshness dots use.
  useEffect(() => {
    if (!core) return;
    const resume = () => {
      lastInput.current = Date.now();
      core.SDL2?.audioContext?.resume().catch(() => {});
    };
    window.addEventListener("pointerdown", resume);
    window.addEventListener("keydown", resume);
    return () => {
      window.removeEventListener("pointerdown", resume);
      window.removeEventListener("keydown", resume);
    };
  }, [core]);

  useEffect(() => {
    if (!core) return;
    const onVisibility = () => {
      if (!currentRef.current || userPaused.current) return;
      const target = ejsSession.current ?? core;
      if (document.hidden) {
        if ("pauseGame" in target) target.pauseGame();
        else target.pause();
        setIdleSince(new Date());
      } else {
        if ("resumeGame" in target) target.resumeGame();
        else target.resume();
        setIdleSince(null);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [core]);

  useEffect(
    () => (core && current && engine === "mgba" ? bindKeyboardExtras(
          core,
          () => fastForwardRef.current,
          () => (fastForwardOnRef.current ? fastForwardRef.current : 1),
        ) : undefined),
    [core, current, engine],
  );

  // The running game and its active slot live in the query string so a
  // reload comes back to the same place.
  useEffect(() => {
    const url = new URL(window.location.href);
    url.searchParams.delete("pick");
    if (current) url.searchParams.delete("pending");
    if (current) url.searchParams.delete("paused");
    if (current) url.searchParams.set("game", current);
    else url.searchParams.delete("game");
    if (current && activeSlot) url.searchParams.set("slot", String(activeSlot));
    else url.searchParams.delete("slot");
    window.history.replaceState(window.history.state, "", url);
  }, [current, activeSlot]);

  const romPath = useCallback((c: MgbaCore, name: string) => `${c.filePaths().gamePath}/${name}`, []);

  // An N64 zip added before zips were read by their
  // contents can sit in mGBA's /data, listed and started as a GBA game. Once
  // per page, before anything starts, such zips move to EmulatorJS's store.
  const [sorted, setSorted] = useState(false);
  useEffect(() => {
    if (!core || status !== "ready" || !ejsReady || sorted) return;
    (async () => {
      let moved = false;
      // No Game Boy game is over 64 MB; a bigger file here (say an N64 .7z
      // picked before CloudBoy remembered systems) takes its system from the
      // cloud library entry with the same base name.
      const huge = core
        .listRoms()
        .filter((n) => !rememberedSystem(n) && core.FS.stat(romPath(core, n)).size > 64 * 1024 * 1024);
      if (huge.length) {
        const cloud = await listCloudRoms().catch(() => []);
        for (const name of huge) {
          const match = cloud.find((rom) => romBaseName(rom.name) === romBaseName(name));
          if (match && isEjsSystem(match.system)) rememberSystem(name, match.system);
        }
      }
      for (const name of core.listRoms().filter((n) => /\.zip$/i.test(n) || rememberedSystem(n))) {
        const blob = new Blob([core.FS.readFile(romPath(core, name)) as BlobPart]);
        if ((await ejsSystemOf(name, blob)) === null) continue;
        await ejsRoms.put(name, blob);
        core.FS.unlink(romPath(core, name));
        moved = true;
      }
      if (moved) {
        await core.FSSync();
        setMgbaRoms(core.listRoms());
        setEjsNames(await ejsRoms.list());
      }
    })()
      .catch(() => {})
      .finally(() => setSorted(true));
  }, [core, status, ejsReady, sorted, romPath]);

  const saveStats = useCallback((): SaveStats | null => {
    const bytes = core && currentRef.current && gameKind.current ? core.getSave() : null;
    return bytes ? parseSaveStats(bytes, gameKind.current!) : null;
  }, [core]);

  const writeState = useCallback(
    async (slot: number) => {
      const rom = currentRef.current;
      if (!core || !rom) return false;
      if (engineRef.current === "ejs") {
        const bytes = ejsSession.current?.getState();
        if (!bytes) return false;
        await ejsStates.put(stateFileName(rom, slot), bytes);
        setEjsStateTimes((times) => ({ ...times, [slot]: new Date() }));
      } else {
        if (!core.saveState(slot)) return false;
        await core.FSSync();
      }
      {
        const stats = saveStats();
        const entry: SlotStats = { at: Date.now(), trainer: stats?.trainer ?? null, playTime: stats?.playTime ?? null };
        slotStore.setStats(rom, slot, entry);
        setSlotStats((all) => ({ ...all, [slot]: entry }));
      }
      bumpSaves();
      return true;
    },
    [core, saveStats, bumpSaves],
  );

  const registerSlot = useCallback((rom: string, slot: number) => {
    setSlots((current) => {
      if (current.includes(slot)) return current;
      const next = [...current, slot].sort((a, b) => a - b);
      slotStore.setList(rom, next);
      return next;
    });
  }, []);

  const activateSlot = useCallback(
    (slot: number) => {
      const rom = currentRef.current!;
      registerSlot(rom, slot);
      setActiveSlot(slot);
      slotStore.setActive(rom, slot);
    },
    [registerSlot],
  );

  const addSlot = useCallback(() => {
    const rom = currentRef.current;
    if (rom) registerSlot(rom, Math.max(0, ...slots) + 1);
  }, [registerSlot, slots]);

  const removeSlot = useCallback(
    async (slot: number) => {
      const rom = currentRef.current;
      if (!core || !rom) return;
      const file = `${core.filePaths().saveStatePath}/${stateFileName(rom, slot)}`;
      if (engineRef.current === "ejs") {
        await ejsStates.remove(stateFileName(rom, slot));
        setEjsStateTimes((times) => Object.fromEntries(Object.entries(times).filter(([k]) => Number(k) !== slot)));
      } else if (core.FS.analyzePath(file).exists) {
        core.FS.unlink(file);
        await core.FSSync();
      }
      slotStore.forget(rom, slot);
      setSlots((current) => {
        const next = current.filter((n) => n !== slot);
        slotStore.setList(rom, next);
        return next;
      });
      const without = <T,>(record: Record<number, T>) => Object.fromEntries(Object.entries(record).filter(([k]) => Number(k) !== slot));
      setSlotNames(without);
      setSlotStats(without);
      setActiveSlot((active) => (active === slot ? null : active));
    },
    [core],
  );

  // Auto-save writes the active slot while the player is active; an idle
  // player has nothing new to save, and until the slot's own state has
  // been loaded into a game that just opened there is only the fresh boot
  // to save, which would write over it. The visibility handler above has
  // already paused the game by the time the tab is hidden, so that save and
  // the one on pagehide capture the state the player last saw.
  const resuming = useRef(false);
  useEffect(() => {
    if (!core || !current || !autoSave || !activeSlot) return;
    const save = () => {
      if (resuming.current || Date.now() - lastInput.current > USER_IDLE_MS) return;
      writeState(activeSlot).then((ok) => ok && setLastAutoSave(new Date()));
    };
    const tick = () => {
      if (!userPaused.current && !document.hidden) save();
    };
    const onVisibility = () => {
      if (document.hidden) save();
    };
    const timer = window.setInterval(tick, AUTO_SAVE_MS);
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", save);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", save);
    };
  }, [core, current, autoSave, activeSlot, writeState]);

  // A game with no slot yet gets slot 1 as its active slot, with auto-save
  // on, so progress is kept without any setup. A game whose slots were all
  // deleted has an empty list stored and is left alone. Same for every engine.
  const initSlots = useCallback((name: string) => {
    if (!slotStore.known(name)) {
      slotStore.setList(name, [1]);
      slotStore.setActive(name, 1);
      setPref(AUTO_SAVE_KEY, "1");
      setAutoSaveState(true);
      autoSaveRef.current = true;
    }
    const knownSlots = slotStore.list(name);
    setSlots(knownSlots);
    setActiveSlot(slotStore.active(name));
    setSlotNames(slotStore.names(name, knownSlots));
    setSlotStats(slotStore.stats(name, knownSlots));
    setLastAutoSave(null);
  }, []);

  useEffect(() => {
    beforeLeaving.current =
      engine === "ejs" && autoSave && activeSlot ? () => (resuming.current ? Promise.resolve(false) : writeState(activeSlot)) : null;
  }, [engine, autoSave, activeSlot, writeState]);

  // With auto-save on, the active slot is the latest position, so a game
  // opens there: on the slot named in the URL, or else the one it was last
  // on. The same rule for every way in (the URL, the library, an added or
  // downloaded game, a battery save pulled from the cloud) and both engines,
  // so nothing boots from an older in-game save and then auto-saves over
  // the slot. If the state can't be loaded the slot is given up rather than
  // written over. The core starts its thread on the tick after loadGame
  // and loadState fails until then, hence the retry.
  const resumeSlot = useCallback(
    async (engine: Engine, name: string, slot: number | null) => {
      if (!core || !slot || !slotStore.list(name).includes(slot)) return;
      slotStore.setActive(name, slot);
      setActiveSlot(slot);
      if (!autoSaveRef.current) return;
      resuming.current = true;
      try {
        if (engine === "ejs") {
          const state = await ejsStates.get(stateFileName(name, slot)).catch(() => undefined);
          if (state && currentRef.current === name) ejsSession.current?.loadState(state.bytes);
          return;
        }
        if (!core.FS.analyzePath(`${core.filePaths().saveStatePath}/${stateFileName(name, slot)}`).exists) return;
        for (let attempt = 0; attempt < 20; attempt++) {
          if (currentRef.current !== name) return;
          if (core.loadState(slot)) return;
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
        if (currentRef.current !== name) return;
        slotStore.clearActive(name);
        setActiveSlot(null);
        setError(`Could not load slot ${slot}; it is kept as it was. Load it from the Saves panel to continue from it.`);
      } finally {
        resuming.current = false;
      }
    },
    [core],
  );

  // The refs are set here as well as on render, so the rest of the start
  // (resuming the slot, which awaits) sees the new game at once.
  const startGame = useCallback(
    (c: MgbaCore, name: string): boolean => {
      // loadGame reads the game's cheat file itself; the file is rewritten
      // from this browser's choice once the ROM can be read for its code.
      const cheatsFile = `${c.filePaths().cheatsPath}/${romBaseName(name)}.cheats`;
      try {
        c.FS.unlink(cheatsFile);
      } catch {
        // No cheats last time.
      }
      const ok = c.loadGame(romPath(c, name));
      if (!ok) {
        setCurrent(null);
        currentRef.current = null;
        setError(`Could not load ${name}.`);
        return false;
      }
      const code = romGameCode(c, name);
      gameCode.current = code;
      const presets = cheatPresets(code, name);
      const chosen = code ? loadCheatChoice(code) : [];
      setCheatsState(presets);
      setCheatChoice(chosen);
      const text = cheatsFileText(presets, chosen);
      if (text) {
        c.FS.writeFile(cheatsFile, new TextEncoder().encode(text));
        c.autoLoadCheats();
      }
      applyKeymap(c);
      c.setVolume(mutedRef.current ? 0 : 1);
      // GBA Pokémon saves identify themselves by sector signature; Game Boy
      // ones need the cartridge title to pick a layout.
      gameKind.current =
        fileExtension(name) === "gba"
          ? "gen3"
          : gameKindFromTitle(gameBoyTitle(c.FS.readFile(romPath(c, name))));
      canvasRef.current?.focus();
      userPaused.current = false;
      setPaused(false);
      setIdleSince(null);
      setCurrent(name);
      currentRef.current = name;
      setEngine("mgba");
      engineRef.current = "mgba";
      initSlots(name);
      setError(null);
      return true;
    },
    [romPath, canvasRef, initSlots],
  );

  // Before an mGBA game gives way (to another game, or to nothing): its
  // active slot is written when auto-save is on, as it would be on a tab
  // close, and its unsynced files set off to the cloud.
  const leaveMgbaGame = useCallback(async () => {
    if (engineRef.current !== "mgba" || !currentRef.current) return;
    if (autoSaveRef.current && activeSlotRef.current && !resuming.current) await writeState(activeSlotRef.current);
    flushCloudSync();
  }, [writeState]);

  const leavePage = useCallback((game: string | null) => {
    ejsSession.current?.flushSave();
    const lastState = beforeLeaving.current?.();
    const url = new URL(window.location.href);
    url.searchParams.delete("slot");
    if (game) url.searchParams.set("game", game);
    else url.searchParams.delete("game");
    Promise.all([lastState, flushCloudSyncBeforeLeaving(), new Promise((resolve) => window.setTimeout(resolve, 300))]).then(() =>
      window.location.assign(url),
    );
  }, []);

  // Android drops a backgrounded page's WebGL context to free GPU memory,
  // and neither core restores one: the game runs on behind a frozen canvas.
  // The slot is written and the page reloaded once it's shown, which
  // resumes the game from the URL.
  useEffect(() => {
    if (!core) return;
    let lost = false;
    const recover = async () => {
      if (!lost || document.hidden) return;
      lost = false;
      if (ejsSession.current) return leavePage(currentRef.current);
      await leaveMgbaGame();
      await flushCloudSyncBeforeLeaving();
      window.location.reload();
    };
    const onLost = () => {
      lost = true;
      void recover();
    };
    document.addEventListener("webglcontextlost", onLost, true);
    document.addEventListener("visibilitychange", recover);
    return () => {
      document.removeEventListener("webglcontextlost", onLost, true);
      document.removeEventListener("visibilitychange", recover);
    };
  }, [core, leaveMgbaGame, leavePage]);

  // A context suspended while the page was hidden would stay silent until
  // the next tap.
  useEffect(() => {
    if (!core) return;
    const onShown = () => {
      if (!document.hidden) core.SDL2?.audioContext?.resume().catch(() => {});
    };
    document.addEventListener("visibilitychange", onShown);
    return () => document.removeEventListener("visibilitychange", onShown);
  }, [core]);

  const startEjsGame = useCallback(
    async (c: MgbaCore, name: string, slot: number | null = null): Promise<boolean> => {
      const rom = await ejsRoms.get(name);
      if (!rom) {
        setError(`Could not load ${name}.`);
        return false;
      }
      if (await isRetiredGame(name, rom)) {
        setError(retiredMessage(name));
        return false;
      }
      // Stored before zips were read, a zip with no clue is an N64 game.
      const system = (await ejsSystemOf(name, rom)) ?? "n64";
      if (currentRef.current && engineRef.current === "mgba") c.quitGame();
      // SDL's key listener on window would otherwise press GBA buttons.
      c.toggleInput(false);
      setStarting({ name, system });
      try {
        ejsSession.current = await startEjs(name, rom, system, mutedRef.current, (down) => holdFastForwardRef.current(down));
      } catch (e) {
        c.toggleInput(true);
        setError(e instanceof Error ? e.message : String(e));
        return false;
      } finally {
        setStarting(null);
      }
      userPaused.current = false;
      setPaused(false);
      setIdleSince(null);
      setEngine("ejs");
      engineRef.current = "ejs";
      setEjsSystem(system);
      setCurrent(name);
      currentRef.current = name;
      initSlots(name);
      const times: Record<number, Date> = {};
      await Promise.all(
        slotStore.list(name).map(async (n) => {
          const state = await ejsStates.get(stateFileName(name, n)).catch(() => undefined);
          if (state) times[n] = new Date(state.at);
        }),
      );
      setEjsStateTimes(times);
      await resumeSlot("ejs", name, slotToOpen(name, slot));
      setError(null);
      return true;
    },
    [initSlots, resumeSlot],
  );

  useEffect(() => {
    if (!core || status !== "ready" || !ejsReady || !sorted || handledSearch.current === routerSearch) return;
    handledSearch.current = routerSearch;
    const params = new URLSearchParams(routerSearch);
    const game = params.get("game");
    const slot = Number(params.get("slot")) || null;
    if (!game || game === currentRef.current) return;
    // `paused=1` (the library's "open paused"): once the game and its slot
    // are loaded, it waits on the pause screen for Resume.
    const settle = (started: Promise<unknown>) => {
      if (params.get("paused") !== "1") return;
      void started.then(() => {
        if (currentRef.current !== game || userPaused.current) return;
        if (ejsSession.current) ejsSession.current.pause();
        else core.pauseGame();
        userPaused.current = true;
        setPaused(true);
        setIdleSince(new Date());
      });
    };
    if (ejsNames.includes(game)) {
      settle(startEjsGame(core, game, slot));
      return;
    }
    if (!core.listRoms().includes(game) || !startGame(core, game)) return;
    settle(resumeSlot("mgba", game, slotToOpen(game, slot)));
  }, [core, status, routerSearch, startGame, startEjsGame, resumeSlot, ejsNames, ejsReady, sorted]);

  // Playing the running game again is a restart: it boots from the in-game
  // save instead of the slot, which is written first so nothing is lost,
  // and no slot is active (so auto-save can't write the restart over it)
  // until the player saves or loads one.
  const play = useCallback(
    async (name: string) => {
      if (!core) return false;
      if (ejsSession.current) {
        leavePage(name);
        return true;
      }
      const restart = name === currentRef.current;
      await leaveMgbaGame();
      if (ejsNames.includes(name) || ejsSystemOfName(name) !== null) return startEjsGame(core, name);
      if (!startGame(core, name)) return false;
      if (restart) {
        slotStore.clearActive(name);
        setActiveSlot(null);
        flash(
          autoSaveRef.current
            ? "Restarted from the in-game save; auto-save waits until you save to or load a slot."
            : "Restarted from the in-game save.",
        );
      } else {
        await resumeSlot("mgba", name, slotStore.active(name));
      }
      return true;
    },
    [core, ejsNames, startGame, startEjsGame, resumeSlot, leaveMgbaGame, leavePage, flash],
  );

  const addRom = useCallback(
    async (file: File) => {
      if (!core) return;
      if (await isRetiredGame(file.name, file)) {
        setError(retiredMessage(file.name));
        return;
      }
      if (!isRomFile(file.name) && ejsSystemOfName(file.name) === null) {
        setError(`${file.name} is not a Game Boy or N64 game (or a .zip of one).`);
        return;
      }
      // Signed in, an added game also goes to the shared library (skipped
      // when it is already there, e.g. it was just downloaded from it).
      const share = session.bearer()
        ? uploadToLibrary(file).then(
            (result) => {
              if (result !== "uploaded") return;
              libraryKeys.forEach((queryKey) => queryClient.invalidateQueries({ queryKey }));
              flash(`${file.name} added to the shared library.`);
            },
            () => flash(`${file.name} is in ${here}, but could not be added to the shared library.`),
          )
        : Promise.resolve();
      if ((await ejsSystemOf(file.name, file)) !== null) {
        await ejsRoms.put(file.name, file);
        setEjsNames(await ejsRoms.list());
        setError(null);
        // Leaving reloads the page; let the upload finish first.
        if (ejsSession.current) await share.finally(() => leavePage(file.name));
        else {
          await leaveMgbaGame();
          await startEjsGame(core, file.name);
        }
        return;
      }
      // Game Boy games are at most 32 MB; anything far bigger is a disc or
      // another system's game mGBA would run out of memory on.
      if (file.size > 64 * 1024 * 1024) {
        setError(`${file.name} is too big for a Game Boy game.`);
        return;
      }
      await leaveMgbaGame();
      await new Promise<void>((resolve) => core.uploadRom(file, resolve));
      await core.FSSync();
      setMgbaRoms(core.listRoms());
      setError(null);
      if (startGame(core, file.name)) await resumeSlot("mgba", file.name, slotStore.active(file.name));
    },
    [core, startGame, startEjsGame, resumeSlot, leaveMgbaGame, leavePage, flash, queryClient],
  );

  // A game named in the URL that is not in this browser is fetched from the
  // cloud library, then imported and started like any added ROM.
  useEffect(() => {
    if (!core || status !== "ready" || !ejsReady) return;
    const game = new URLSearchParams(routerSearch).get("game");
    if (!game || fetchedFor.current === game) return;
    if (game === currentRef.current || ejsNames.includes(game) || core.listRoms().includes(game)) return;
    fetchedFor.current = game;
    (async () => {
      try {
        const rom = (await listCloudRoms()).find((r) => r.name === game);
        if (!rom) {
          setError(`${game} is not in ${here} or your cloud library.`);
          return;
        }
        if (isNative(rom.system)) {
          setError(`${displayName(game)} plays in the CloudBoy desktop app.`);
          return;
        }
        setFetching({ name: game, fraction: 0 });
        const file = await downloadCloudRom(rom, (fraction) => setFetching({ name: game, fraction }));
        setFetching(null);
        await addRom(file);
      } catch (e) {
        setFetching(null);
        setError(
          needsSignIn(e)
            ? `${game} is not in ${here}. Sign in to download it from your cloud library.`
            : `Could not download ${game}.`,
        );
      }
    })();
  }, [core, status, ejsReady, routerSearch, ejsNames, addRom]);

  const deleteRom = useCallback(
    async (name: string) => {
      if (!core) return;
      if (ejsNames.includes(name)) {
        await ejsRoms.remove(name);
        setEjsNames(await ejsRoms.list());
        if (currentRef.current === name) leavePage(null);
        return;
      }
      if (currentRef.current === name) {
        core.quitGame();
        setCurrent(null);
        setActiveSlot(null);
      }
      core.FS.unlink(romPath(core, name));
      await core.FSSync();
      setMgbaRoms(core.listRoms());
    },
    [core, romPath, ejsNames, leavePage],
  );

  const stop = useCallback(async () => {
    if (!core || !currentRef.current) return;
    // EmulatorJS can't stop; the caller leaves the page once this has
    // written the game save and the active slot.
    if (engineRef.current === "ejs") {
      ejsSession.current?.flushSave();
      await Promise.all([beforeLeaving.current?.(), flushCloudSyncBeforeLeaving()]);
      return;
    }
    await leaveMgbaGame();
    core.quitGame();
    setCurrent(null);
    setActiveSlot(null);
    window.clearTimeout(syncTimer.current);
    await core.FSSync();
  }, [core, leaveMgbaGame]);

  const togglePause = useCallback(() => {
    if (!core || !currentRef.current) return;
    const ejs = ejsSession.current;
    if (userPaused.current) {
      if (ejs) ejs.resume();
      else core.resumeGame();
    } else if (ejs) ejs.pause();
    else core.pauseGame();
    setIdleSince(userPaused.current ? null : new Date());
    userPaused.current = !userPaused.current;
    setPaused(userPaused.current);
  }, [core]);

  // The console's reset button. The active slot is written first and then
  // let go, as for a restart: with auto-save on it would otherwise get the
  // title screen 30 seconds later in place of where the player was.
  const resetConsole = useCallback(async () => {
    const name = currentRef.current;
    if (!core || !name) return;
    const slot = activeSlotRef.current;
    if (slot) await writeState(slot);
    if (ejsSession.current) ejsSession.current.reset();
    else core.quickReload();
    slotStore.clearActive(name);
    setActiveSlot(null);
    if (userPaused.current) togglePause();
    flash(
      slot && autoSaveRef.current
        ? `Console reset. Slot ${slot} kept where you were; auto-save waits until you save to or load a slot.`
        : "Console reset.",
    );
  }, [core, writeState, togglePause, flash]);

  const ejsInput = useCallback((index: number, value: number) => ejsSession.current?.input(index, value), []);

  const setFastForward = useCallback((multiplier: number) => {
    setFastForwardState(multiplier);
    setPref(FAST_FORWARD_KEY, String(multiplier));
  }, []);

  const toggleFastForward = useCallback(() => setFastForwardOn((on) => !on), []);

  const setEjsMenu = useCallback((open: boolean) => ejsSession.current?.setMenu(open), []);

  const holdFastForward = useCallback(
    (down: boolean) => {
      const speed = down || fastForwardOnRef.current ? fastForwardRef.current : 1;
      if (engine === "ejs") ejsSession.current?.setSpeed(speed);
      else if (core && engine === "mgba") core.setFastForwardMultiplier(speed);
    },
    [core, engine],
  );
  holdFastForwardRef.current = holdFastForward;

  // Applies the latched speed; also re-applies after a game load and when
  // the chosen multiplier changes while it is on.
  useEffect(() => {
    if (engine === "ejs" && current) ejsSession.current?.setSpeed(fastForwardOn ? fastForward : 1);
    else if (core && current && engine === "mgba") core.setFastForwardMultiplier(fastForwardOn ? fastForward : 1);
  }, [core, current, engine, fastForwardOn, fastForward]);

  const toggleMute = useCallback(() => {
    const next = !mutedRef.current;
    setMuted(next);
    setPref(MUTED_KEY, next ? "1" : "0");
    if (ejsSession.current) ejsSession.current.setMuted(next);
    else core?.setVolume(next ? 0 : 1);
  }, [core]);

  const renameSlot = useCallback((slot: number, name: string) => {
    const rom = currentRef.current;
    if (!rom) return;
    const trimmed = name.trim();
    setSlotNames((names) => {
      const next = { ...names };
      if (trimmed) next[slot] = trimmed;
      else delete next[slot];
      return next;
    });
    slotStore.rename(rom, slot, trimmed);
  }, []);

  const slotLabel = useCallback((slot: number) => slotNames[slot] ?? `Slot ${slot}`, [slotNames]);

  const lastInputAt = useCallback(() => lastInput.current, []);

  const stateSavedAt = useCallback(
    (slot: number): Date | null => {
      const name = currentRef.current;
      if (!core || !name) return null;
      if (engineRef.current === "ejs") return ejsStateTimes[slot] ?? null;
      const file = `${core.filePaths().saveStatePath}/${stateFileName(name, slot)}`;
      return core.FS.analyzePath(file).exists ? core.FS.stat(file).mtime : null;
    },
    [core, ejsStateTimes],
  );

  // SDL's key listener on window sees every keystroke on the page; switch
  // it off while a text field has focus so typing doesn't press buttons.
  const captureKeyboard = useCallback((enabled: boolean) => core?.toggleInput(enabled), [core]);

  const setAutoSave = useCallback((on: boolean) => {
    setAutoSaveState(on);
    setPref(AUTO_SAVE_KEY, on ? "1" : "0");
  }, []);

  const currentSaveBytes = useCallback(() => (core && currentRef.current ? core.getSave() : null), [core]);

  const exportSave = useCallback(() => {
    const name = currentRef.current;
    const bytes = currentSaveBytes();
    if (!name || !bytes) {
      flash("No battery save yet. Save in-game first.");
      return;
    }
    downloadBytes(saveFileName(name), bytes);
  }, [currentSaveBytes, flash]);

  // The current game's battery save and every state file on disk (not just
  // the registered slots, so sync sees exactly what is there), with the
  // file mtimes the cloud panel compares against cloud timestamps.
  const localSaves = useCallback((): LocalSave[] => {
    const name = currentRef.current;
    if (!core || !name) return [];
    // EmulatorJS: just the game save (N64 cartridge memory), under the same
    // <game>.sav name. Its states stay in {here} (ejsStates).
    if (engineRef.current === "ejs") {
      const bytes = ejsSession.current?.getSave();
      return bytes ? [{ name: saveFileName(name), updatedAt: new Date(), bytes }] : [];
    }
    const { savePath, saveStatePath } = core.filePaths();
    const files: LocalSave[] = [];
    const battery = core.getSave();
    const batteryFile = `${savePath}/${saveFileName(name)}`;
    if (battery) {
      const updatedAt = core.FS.analyzePath(batteryFile).exists ? core.FS.stat(batteryFile).mtime : new Date();
      files.push({ name: saveFileName(name), updatedAt, bytes: battery });
    }
    for (const entry of core.FS.readdir(saveStatePath)) {
      if (stateSlotOf(entry) === null || !isSaveFileFor(name, entry)) continue;
      const file = `${saveStatePath}/${entry}`;
      files.push({ name: entry, updatedAt: core.FS.stat(file).mtime, bytes: core.FS.readFile(file) });
    }
    return files;
  }, [core]);

  const importCloudFile = useCallback(
    async (file: string, bytes: Uint8Array) => {
      const name = currentRef.current;
      if (!core || !name) return false;
      if (engineRef.current === "ejs") {
        if (file !== saveFileName(name) || !ejsSession.current) return false;
        const current = ejsSession.current.getSave();
        if (current && !sameBytes(current, bytes)) await keepSave(name, current, "cloud");
        ejsSession.current.putSave(bytes);
        return true;
      }
      // A battery save only takes effect from a fresh boot; the game then
      // opens on its slot again, as it would from the URL, since a state
      // pulled with it is the newer position.
      if (file === saveFileName(name)) {
        await keepMgbaBattery(core, name, bytes, "cloud");
        core.quitGame();
        core.FS.writeFile(`${core.filePaths().savePath}/${file}`, bytes);
        await core.FSSync();
        if (startGame(core, name)) await resumeSlot("mgba", name, slotStore.active(name));
        return true;
      }
      core.FS.writeFile(`${core.filePaths().saveStatePath}/${file}`, bytes);
      await core.FSSync();
      const slot = stateSlotOf(file);
      if (slot !== null) registerSlot(name, slot);
      return true;
    },
    [core, startGame, resumeSlot, registerSlot],
  );

  // Replace the battery save with the file, restart from it, and snapshot
  // that boot into the slot, so loading the slot returns to exactly this
  // save. The core starts its thread on the tick after loadGame and
  // saveState fails until then, hence the retry.
  const importSaveToSlot = useCallback(
    async (slot: number, file: File) => {
      const name = currentRef.current;
      if (!core || !name) return;
      const bytes = new Uint8Array(await file.arrayBuffer());
      await keepMgbaBattery(core, name, bytes, "import");
      core.quitGame();
      core.FS.writeFile(`${core.filePaths().savePath}/${saveFileName(name)}`, bytes);
      await core.FSSync();
      if (!startGame(core, name)) return;
      for (let attempt = 0; attempt < 20; attempt++) {
        await new Promise((resolve) => window.setTimeout(resolve, 100));
        if (await writeState(slot)) {
          activateSlot(slot);
          flash(`${file.name} imported into slot ${slot}.`);
          return;
        }
      }
      setError(`Imported ${file.name}, but could not write state ${slot}.`);
    },
    [core, startGame, writeState, activateSlot, flash],
  );

  const saveState = useCallback(
    async (slot: number) => {
      if (!core || !currentRef.current) return;
      if (!(await writeState(slot))) {
        setError(`Could not write state ${slot}.`);
        return;
      }
      activateSlot(slot);
      flash(`State ${slot} saved.`);
    },
    [core, writeState, activateSlot, flash],
  );

  // One press: the slot is written, then the game's saves go up without
  // waiting for the next sync pass. N64 states are too big for the cloud,
  // so for them only the cartridge save goes up.
  const quickSave = useCallback(async (): Promise<"synced" | "local" | "failed"> => {
    if (!core || !currentRef.current) return "failed";
    const slot = activeSlotRef.current ?? slots[0] ?? 1;
    const label = slotLabel(slot);
    if (!(await writeState(slot))) {
      setError(`Could not write ${label}.`);
      return "failed";
    }
    activateSlot(slot);
    flash(`${label} saved. Uploading…`);
    const result = await syncNow();
    const ejs = engineRef.current === "ejs";
    flash(
      result === "synced"
        ? ejs
          ? `${label} saved; the cartridge save is in the cloud (N64 states stay in ${here}).`
          : `${label} saved and uploaded to the cloud.`
        : result === "held"
          ? `${label} saved here. The cloud has newer saves from another device, so nothing went up; see Saves.`
          : result === "failed"
            ? `${label} saved here, but the upload failed. It will try again.`
            : result === "offline"
              ? `${label} saved in ${here}. You're offline; it goes up when you're back online.`
              : `${label} saved in ${here}.`,
    );
    return result === "synced" ? "synced" : result === "failed" ? "failed" : "local";
  }, [core, slots, slotLabel, writeState, activateSlot, flash]);

  const loadState = useCallback(
    async (slot: number) => {
      const rom = currentRef.current;
      if (!core || !rom) return;
      if (engineRef.current === "ejs") {
        const state = await ejsStates.get(stateFileName(rom, slot)).catch(() => undefined);
        if (state) {
          ejsSession.current?.loadState(state.bytes);
          activateSlot(slot);
          flash(`State ${slot} loaded.`);
        } else {
          flash(`No state in slot ${slot}.`);
        }
        return;
      }
      if (core.loadState(slot)) {
        activateSlot(slot);
        flash(`State ${slot} loaded.`);
      } else {
        flash(`No state in slot ${slot}.`);
      }
    },
    [core, activateSlot, flash],
  );

  // mGBA can add cheat sets to a running game but not take them away, so
  // the game is loaded again with the new file, its position carried over
  // in a scratch state. Auto-save waits, as on a resume, so the fresh boot
  // is never written over the active slot.
  const setCheats = useCallback(
    async (ids: CheatId[]) => {
      const name = currentRef.current;
      const code = gameCode.current;
      if (!core || !name || !code || engineRef.current !== "mgba") return;
      saveCheatChoice(code, ids);
      resuming.current = true;
      try {
        const kept = core.saveState(SCRATCH_SLOT);
        if (!startGame(core, name) || !kept) return;
        for (let attempt = 0; attempt < 20; attempt++) {
          if (currentRef.current !== name) return;
          if (core.loadState(SCRATCH_SLOT)) break;
          await new Promise((resolve) => window.setTimeout(resolve, 100));
        }
        try {
          core.FS.unlink(`${core.filePaths().saveStatePath}/${stateFileName(name, SCRATCH_SLOT)}`);
        } catch {
          // Never written.
        }
      } finally {
        resuming.current = false;
      }
    },
    [core, startGame],
  );

  return {
    core,
    status,
    engine,
    version,
    error,
    notice,
    library,
    starting,
    fetching,
    current,
    paused,
    fastForward,
    fastForwardOn,
    toggleFastForward,
    ejsSystem,
    setEjsMenu,
    holdFastForward,
    setFastForward,
    activeSlot,
    slots,
    addSlot,
    removeSlot,
    slotNames,
    renameSlot,
    slotLabel,
    stateSavedAt,
    saveStats,
    slotStats,
    muted,
    toggleMute,
    idleSince,
    lastInputAt,
    captureKeyboard,
    autoSave,
    lastAutoSave,
    setAutoSave,
    addRom,
    deleteRom,
    play,
    stop,
    togglePause,
    exportSave,
    importSaveToSlot,
    currentSaveBytes,
    localSaves,
    saveRevision,
    importCloudFile,
    saveState,
    cheats,
    cheatChoice,
    setCheats,
    quickSave,
    resetConsole,
    ejsInput,
    loadState,
  };
}
